import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Space,
  Table,
} from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  partnerCommissionSelector,
  partnerPaymentSelector,
  partnerSummarySelector,
} from "../../feature/partner-details/partnerDetailsSelector";
import {
  createPartnerPaymentRequest,
  getPartnerCommissionRequest,
  getPartnerPaymentRequest,
  getPartnerSummaryRequest,
  removePartnerCommissionRequest,
  removePartnerPaymentRequest,
  updatePartnerPaymentRequest,
} from "../../feature/partner-details/partnerDetailsSlice";
import { appConfig } from "../../utils/appConfig";
import { parseToMoney } from "../../utils/helper";

const Ledger = ({ isPartner, id }) => {
  const [commissionPage, setCommissionPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [startDate, setStartDate] = useState(moment("2022-01-01"));
  const [endDate, setEndDate] = useState(moment(new Date()));
  const [commissionPageSize, setCommissionPageSize] = useState(
    appConfig.PAGE_SIZE
  );
  const [paymentPageSize, setPaymentPageSize] = useState(appConfig.PAGE_SIZE);
  const [selectedPartnerCommission, setSelectedPartnerCommission] = useState();
  const [selectedPayment, setSelectedPayment] = useState();
  const [selectedPaymentRemove, setSelectedPaymentRemove] = useState();
  const [displayRemoveModal, setDisplayRemoveModal] = useState(false);
  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [displayPaymentRemoveModal, setDisplayPaymentRemoveModal] =
    useState(false);
  const {
    listPartnerCommission,
    isGetPartnerCommission,
    commissionTotal,
    totalEarnedRange,
    totalClawbackRange,
  } = useSelector(({ partnerDetailsState }) =>
    partnerCommissionSelector(partnerDetailsState)
  );
  const {
    listPartnerPayment,
    isGetPartnerPayment,
    isUpdatePartnerPayment,
    paymentTotal,
    totalAmountRange,
  } = useSelector(({ partnerDetailsState }) =>
    partnerPaymentSelector(partnerDetailsState)
  );
  const { totalEarned, totalPayment, totalClawback } = useSelector(
    ({ partnerDetailsState }) => partnerSummarySelector(partnerDetailsState)
  );
  const dispatch = useDispatch();
  const commissionTotalRef = useRef(commissionTotal);
  const paymentTotalRef = useRef(paymentTotal);
  const [form] = Form.useForm();

  const commissionEarnedColumns = useMemo(() => {
    const result = [
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (time) => moment(time).format("DD MMM YYYY"),
      },
      {
        title: "User Name",
        dataIndex: "userInfo",
        key: "userInfo",
        render: (value) =>
          value &&
          value.length > 0 &&
          `${value[0].userFirstName || ""} ${value[0].userLastName || ""}`,
      },
      {
        title: "User Email",
        dataIndex: "userEmail",
        key: "userEmail",
      },
      {
        title: "Product",
        dataIndex: "metadata",
        key: "metadata",
        render: (value) => value?.productName,
      },
      {
        title: "Paid Amount",
        dataIndex: "paidAmount",
        key: "paidAmount",
        render: (value, item) =>
          value
            ? item.earnedAmount < 0
              ? parseToMoney(value * -1)
              : parseToMoney(value)
            : "",
      },
      {
        title: "Commission Amount",
        dataIndex: "earnedAmount",
        key: "earnedAmount",
        render: (value) => (value ? parseToMoney(value) : ""),
      },
      {
        title: "Stripe Invoice#",
        dataIndex: "invoiceNumber",
        key: "invoiceNumber",
        render: (value, item) => {
          if (value) {
            return (
              <a href={item.invoice} target="_blank" rel="noreferrer">
                {value}
              </a>
            );
          }
          if (item.receipt) {
            return (
              <a href={item.receipt} target="_blank" rel="noreferrer">
                View
              </a>
            );
          }
          return "N/A";
        },
      },
      {
        title: "Action",
        key: "action",
        render: (_, item) => (
          <Space size="middle">
            <span
              className="pointer ant-typography ant-typography-danger"
              onClick={() => onRemovePartnerCommission(item)}
            >
              Delete
            </span>
          </Space>
        ),
      },
    ];
    return isPartner
      ? result.filter(
          (item) =>
            item.key !== "action" &&
            item.key !== "invoiceNumber" &&
            item.key !== "userEmail"
        )
      : result;
  }, [isPartner]);

  const paymentsMadeColumns = useMemo(() => {
    const result = [
      {
        title: "Date",
        dataIndex: "dateSorted",
        key: "dateSorted",
        render: (time) => moment(time).format("DD MMM YYYY"),
      },
      {
        title: "Note",
        dataIndex: "note",
        key: "note",
      },
      {
        title: "Paid Amount",
        dataIndex: "amount",
        key: "amount",
        render: (value) => (value ? parseToMoney(value) : ""),
      },
      {
        title: "Action",
        key: "action",
        render: (_, item) => (
          <Space size="middle">
            <span
              className="link-pointer"
              onClick={() => {
                setSelectedPayment(item);
                setDisplayPaymentModal(true);
              }}
            >
              Edit
            </span>
            <span
              className="pointer ant-typography ant-typography-danger"
              onClick={() => {
                setSelectedPaymentRemove(item);
                setDisplayPaymentRemoveModal(true);
              }}
            >
              Delete
            </span>
          </Space>
        ),
      },
    ];
    return isPartner ? result.filter((item) => item.key !== "action") : result;
  }, [isPartner]);

  const rangeDate = () => {
    return `${moment(startDate).format("DD MMM YYYY")} - ${moment(
      endDate
    ).format("DD MMM YYYY")}`;
  };

  const onRemovePartnerCommission = (data) => {
    setSelectedPartnerCommission(data);
    setDisplayRemoveModal(true);
  };

  const onConfirmRemove = () => {
    selectedPartnerCommission &&
      dispatch(removePartnerCommissionRequest(selectedPartnerCommission));
    setDisplayRemoveModal(false);
  };

  const onConfirmPaymentRemove = () => {
    selectedPaymentRemove &&
      dispatch(removePartnerPaymentRequest(selectedPaymentRemove));
    setDisplayPaymentRemoveModal(false);
  };

  const onUpdatePayment = (values) => {
    if (selectedPayment) {
      dispatch(
        updatePartnerPaymentRequest({
          ...values,
          partner: id,
          _id: selectedPayment._id,
        })
      );
    } else {
      dispatch(createPartnerPaymentRequest({ ...values, partner: id }));
    }
  };

  const loadCommissionData = (newPage, sizeChanger) => {
    const limit = sizeChanger || commissionPageSize;
    const st = moment(startDate).endOf("day").toISOString();
    const et = moment(endDate).endOf("day").toISOString();
    setCommissionPage(newPage);
    setCommissionPageSize(sizeChanger);
    dispatch(
      getPartnerCommissionRequest({
        id,
        st,
        et,
        limit,
        offset: (newPage - 1) * limit,
      })
    );
  };

  const loadPaymentData = (newPage, sizeChanger) => {
    const limit = sizeChanger || paymentPageSize;
    const st = moment(startDate).endOf("day").toISOString();
    const et = moment(endDate).endOf("day").toISOString();
    setPaymentPage(newPage);
    setPaymentPageSize(sizeChanger);
    dispatch(
      getPartnerPaymentRequest({
        id,
        st,
        et,
        limit,
        offset: (newPage - 1) * limit,
      })
    );
  };

  const onFilter = () => {
    loadCommissionData(1, commissionPageSize);
    loadPaymentData(1, paymentPageSize);
  };

  const onChangeCommissionTable = (page, sizeChanger) => {
    loadCommissionData(page, sizeChanger);
  };

  const onChangePaymentTable = (page, sizeChanger) => {
    loadPaymentData(page, sizeChanger);
  };

  useEffect(() => {
    if (
      commissionTotal < commissionTotalRef.current &&
      commissionPage !== commissionTotal / commissionPageSize &&
      commissionPage > 1 &&
      commissionTotal > 0 &&
      commissionTotal % commissionPageSize === 0
    ) {
      loadCommissionData(commissionPage - 1, commissionPageSize);
      commissionTotalRef.current = commissionTotal;
      return;
    }
    if (commissionTotal > commissionTotalRef.current) {
      loadCommissionData(1, commissionPageSize);
      commissionTotalRef.current = commissionTotal;
      return;
    }
    loadCommissionData(commissionPage, commissionPageSize);
    commissionTotalRef.current = commissionTotal;
    // eslint-disable-next-line
  }, [commissionTotal]);

  useEffect(() => {
    if (
      paymentTotal < paymentTotalRef.current &&
      paymentPage !== paymentTotal / paymentPageSize &&
      paymentPage > 1 &&
      paymentTotal > 0 &&
      paymentTotal % paymentPageSize === 0
    ) {
      loadPaymentData(paymentPage - 1, paymentPageSize);
      paymentTotalRef.current = paymentTotal;
      return;
    }
    if (paymentTotal > paymentTotalRef.current) {
      loadPaymentData(1, paymentPageSize);
      paymentTotalRef.current = paymentTotal;
      return;
    }
    loadPaymentData(paymentPage, paymentPageSize);
    paymentTotalRef.current = paymentTotal;
    // eslint-disable-next-line
  }, [paymentTotal]);

  useEffect(() => {
    dispatch(getPartnerSummaryRequest(id));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    selectedPayment &&
      form.setFieldsValue({
        date: moment(selectedPayment.dateSorted),
        note: selectedPayment.note,
        amount: selectedPayment.amount,
      });
  }, [selectedPayment, form]);

  return (
    <div className="ledger-container">
      <div className="filter-container mt-25">
        <div className="mr-5">
          <div>Start Date</div>
          <DatePicker
            placeholder="DD MMM YYYY"
            format="DD MMM YYYY"
            onChange={(date) => setStartDate(date)}
            value={startDate}
          />
        </div>
        <div className="mr-25">
          <div>End Date</div>
          <DatePicker
            placeholder="DD MMM YYYY"
            format="DD MMM YYYY"
            onChange={(date) => setEndDate(date)}
            value={endDate}
          />
        </div>
        <div>
          <div className="hidden">filter ledger</div>
          <Button type="primary" onClick={onFilter}>
            Filter Ledger
          </Button>
        </div>
      </div>
      <div className="title-child mt-25">
        {`Commission Earned for Date Range (${rangeDate()})`}
      </div>
      <Table
        className="mt-5"
        columns={commissionEarnedColumns}
        dataSource={listPartnerCommission}
        bordered
        pagination={{
          showTotal: (total) => `Total ${total} results`,
          defaultPageSize: appConfig.PAGE_SIZE,
          commissionPageSize,
          defaultCurrent: 1,
          current: commissionPage,
          showSizeChanger: true,
          pageSizeOptions: [appConfig.PAGE_SIZE, 25, 50].sort((a, b) => a - b),
          total: commissionTotal,
          onChange: (page, sizeChanger) =>
            onChangeCommissionTable(page, sizeChanger),
        }}
        loading={isGetPartnerCommission}
        rowKey="_id"
      />
      <div className="bold text-right">{`Total Commission Earned = ${parseToMoney(
        totalEarnedRange
      )}`}</div>
      <div className="bold text-right">{`Total Clawback = ${parseToMoney(
        totalClawbackRange,
        true
      )}`}</div>
      <div className="bold text-right">{`Balance = ${parseToMoney(
        totalEarnedRange + totalClawbackRange
      )}`}</div>

      <div className="max-w-1100">
        <div className="title-child">{`Payments Made for Date Range (${moment(
          startDate
        ).format("DD MMM YYYY")} - ${moment(endDate).format(
          "DD MMM YYYY"
        )})`}</div>
        {!isPartner && (
          <Button
            type="primary mt-5 f-right mb-5"
            onClick={() => setDisplayPaymentModal(true)}
          >
            Create Payment
          </Button>
        )}
        <Table
          columns={paymentsMadeColumns}
          dataSource={listPartnerPayment}
          bordered
          pagination={{
            showTotal: (total) => `Total ${total} results`,
            defaultPageSize: appConfig.PAGE_SIZE,
            paymentPageSize,
            defaultCurrent: 1,
            current: paymentPage,
            showSizeChanger: true,
            pageSizeOptions: [appConfig.PAGE_SIZE, 25, 50].sort(
              (a, b) => a - b
            ),
            total: paymentTotal,
            onChange: (page, sizeChanger) =>
              onChangePaymentTable(page, sizeChanger),
          }}
          loading={isGetPartnerPayment || isUpdatePartnerPayment}
          rowKey="_id"
        />
        <div className="bold text-right">{`Total Payments Made = ${parseToMoney(
          totalAmountRange
        )}`}</div>

        <div className="d-flex space-between bottom mt-25">
          <div>
            <div className="title-child">{`Total as of ${moment().format(
              "DD MMM YYYY"
            )}`}</div>
            <table>
              <tbody>
                <tr>
                  <th className="pr-5">Total Commission Earned:</th>
                  <td>{`${parseToMoney(totalEarned)}`}</td>
                </tr>
                <tr>
                  <th className="pr-5">Total Clawback:</th>
                  <td>{`${parseToMoney(totalClawback, true)}`}</td>
                </tr>
                <tr>
                  <th className="pr-5">Total Payments:</th>
                  <td>{`${parseToMoney(totalPayment, true)}`}</td>
                </tr>
                <tr>
                  <th className="bold pr-5">Balance:</th>
                  <td className="bold">
                    {`${parseToMoney(
                      totalEarned + totalClawback - totalPayment
                    )}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="title-child">{`Summary for Date Range (${rangeDate()})`}</div>
            <table className="f-right">
              <tbody>
                <tr>
                  <th className="pr-5">Commission Earned:</th>
                  <td>{`${parseToMoney(totalEarnedRange)}`}</td>
                </tr>
                <tr>
                  <th className="pr-5">Clawback:</th>
                  <td>{`${parseToMoney(totalClawbackRange, true)}`}</td>
                </tr>
                <tr>
                  <th className="pr-5">Payments:</th>
                  <td>{`${parseToMoney(totalAmountRange, true)}`}</td>
                </tr>
                <tr>
                  <th className="bold pr-5">Balance:</th>
                  <td className="bold">
                    {`${parseToMoney(
                      totalEarnedRange + totalClawbackRange - totalAmountRange
                    )}`}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {displayRemoveModal && (
        <Modal
          title="Are you sure?"
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onOk={onConfirmRemove}
          onCancel={() => setDisplayRemoveModal(false)}
        >
          <p>
            Do you really want to delete this commission earned? This process
            cannot be undone.
          </p>
        </Modal>
      )}
      {displayPaymentRemoveModal && (
        <Modal
          title="Are you sure?"
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onOk={onConfirmPaymentRemove}
          onCancel={() => setDisplayPaymentRemoveModal(false)}
        >
          <p>
            Do you really want to delete this payment made? This process cannot
            be undone.
          </p>
        </Modal>
      )}
      {displayPaymentModal && (
        <Modal
          title={selectedPayment ? "Update Payment Made" : "Add Payment Made"}
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                onUpdatePayment(values);
                form.resetFields();
                setSelectedPayment(undefined);
                setDisplayPaymentModal(false);
              })
              .catch((info) => {
                message.error("Validate failed!");
                console.log("Validate Failed:", info);
              });
          }}
          onCancel={() => {
            form.resetFields();
            setSelectedPayment(undefined);
            setDisplayPaymentModal(false);
          }}
        >
          <div className="add-tracking-link-container">
            <Form form={form} name="basic" className="row-col">
              <Form.Item
                className="custom-form-item"
                name="date"
                label="Date"
                initialValue={moment(new Date())}
              >
                <DatePicker placeholder="DD MMM YYYY" format="DD MMM YYYY" />
              </Form.Item>
              <Form.Item className="custom-form-item" name="note" label="Note">
                <Input />
              </Form.Item>
              <Form.Item
                className="custom-form-item"
                name="amount"
                label="Paid Amount ($)"
                rules={[
                  { required: true, message: "Please input paid amount!" },
                ]}
              >
                <InputNumber className="w-100-p" />
              </Form.Item>
            </Form>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Ledger;
